import { fetchApi } from '@/services/api-client';
import type { EditorJsContent } from '@/services/editor-js';

const PUBLIC_PATH = '/newsletter';
const ADMIN_PATH = '/newsletter-admin';

export type IssueStatus = 'draft' | 'published';
export type EntryKind = 'front' | 'grade';
export type EntryStatus = 'draft' | 'submitted';
export type SlotStatus = 'not_started' | 'draft' | 'submitted' | 'dismissed';

export interface NewsletterIssue {
  id: string;
  title: string;
  slug: string;
  weekStart: string;
  weekEnd: string;
  status: IssueStatus;
  publishedAt: string | null;
  notifiedAt: string | null;
  notifiedCount?: number;
  /** When the draft publishes itself; null once published or when never scheduled. */
  scheduledPublishAt: string | null;
  /** Email families as part of the scheduled publish. */
  autoSend: boolean;
}

export interface IssueScheduleBody {
  scheduledPublishAt?: string | null;
  autoSend?: boolean;
}

export interface NewsletterEntry {
  id: string;
  kind: EntryKind;
  gradeLevel?: number;
  subjectLabel?: string;
  title: string;
  editorContent?: EditorJsContent;
  content: string;
  status: EntryStatus;
  sortOrder: number;
  author: { id: string; displayName: string };
}

export interface TeacherSlot {
  gradeLevel: number;
  subjectId: string | null;
  subjectLabel: string;
  entryId: string | null;
  status: SlotStatus;
  source: 'timetable' | 'manual';
  dismissalId: string | null;
}

export interface OutstandingTeacher {
  teacherId: string;
  displayName: string;
  email: string | null;
  missing: number;
}

export interface SendPreview {
  recipientCount: number;
  batchCount: number;
  batchSize: number;
  alreadySentAt: string | null;
  subject: string;
  url: string;
}

export interface GradeStats {
  gradeLevel: number;
  views: number;
  uniqueReaders: number;
  averageSeconds: number;
}

export interface IssueStats {
  views: number;
  uniqueReaders: number;
  /** Distinct parent readers only — the population `reachPercent` is computed against. */
  parentReaders: number;
  notifiedCount: number | null;
  reachPercent: number | null;
  averageSeconds: number;
  byGrade: GradeStats[];
}

export interface UpsertEntryBody {
  kind: EntryKind;
  gradeLevel?: number;
  subjectId?: string;
  subjectLabel?: string;
  title: string;
  editorContent?: EditorJsContent;
  content?: string;
}

export const newsletterApi = {
  getIssues: () => fetchApi<NewsletterIssue[]>(`${ADMIN_PATH}/issues`),

  getOpenIssue: () => fetchApi<NewsletterIssue | null>(`${ADMIN_PATH}/issues/open`),

  getMyMissingCount: () => fetchApi<{ count: number }>(`${ADMIN_PATH}/my-missing-count`),

  createIssue: (
    body: { schoolYearId: string; weekStart: string; weekEnd: string; title: string } & IssueScheduleBody
  ) => fetchApi<NewsletterIssue>(`${ADMIN_PATH}/issues`, { method: 'POST', body: JSON.stringify(body) }),

  updateIssue: (issueId: string, body: { title?: string } & IssueScheduleBody) =>
    fetchApi<NewsletterIssue>(`${ADMIN_PATH}/issues/${issueId}`, { method: 'PUT', body: JSON.stringify(body) }),

  getMySlots: (issueId: string) => fetchApi<TeacherSlot[]>(`${ADMIN_PATH}/issues/${issueId}/my-slots`),

  dismissSlot: (issueId: string, body: { gradeLevel: number; subjectId?: string; subjectLabel: string }) =>
    fetchApi<{ id: string }>(`${ADMIN_PATH}/issues/${issueId}/dismissals`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  undismissSlot: (dismissalId: string) =>
    fetchApi<{ success: boolean }>(`${ADMIN_PATH}/dismissals/${dismissalId}`, { method: 'DELETE' }),

  getEntries: (issueId: string) => fetchApi<NewsletterEntry[]>(`${ADMIN_PATH}/issues/${issueId}/entries`),

  createEntry: (issueId: string, body: UpsertEntryBody) =>
    fetchApi<NewsletterEntry>(`${ADMIN_PATH}/issues/${issueId}/entries`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateEntry: (issueId: string, entryId: string, body: UpsertEntryBody) =>
    fetchApi<NewsletterEntry>(`${ADMIN_PATH}/issues/${issueId}/entries/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  submitEntry: (entryId: string) =>
    fetchApi<NewsletterEntry>(`${ADMIN_PATH}/entries/${entryId}/submit`, { method: 'POST' }),

  unsubmitEntry: (entryId: string) =>
    fetchApi<NewsletterEntry>(`${ADMIN_PATH}/entries/${entryId}/unsubmit`, { method: 'POST' }),

  deleteEntry: (entryId: string) =>
    fetchApi<{ success: boolean }>(`${ADMIN_PATH}/entries/${entryId}`, { method: 'DELETE' }),

  reorderFront: (issueId: string, entryIds: string[]) =>
    fetchApi<{ success: boolean }>(`${ADMIN_PATH}/issues/${issueId}/front-order`, {
      method: 'PUT',
      body: JSON.stringify({ entryIds }),
    }),

  getOutstanding: (issueId: string) => fetchApi<OutstandingTeacher[]>(`${ADMIN_PATH}/issues/${issueId}/outstanding`),

  publish: (issueId: string) =>
    fetchApi<NewsletterIssue>(`${ADMIN_PATH}/issues/${issueId}/publish`, { method: 'POST' }),

  unpublish: (issueId: string) =>
    fetchApi<NewsletterIssue>(`${ADMIN_PATH}/issues/${issueId}/unpublish`, { method: 'POST' }),

  getSendPreview: (issueId: string) => fetchApi<SendPreview>(`${ADMIN_PATH}/issues/${issueId}/send-preview`),

  send: (issueId: string) => fetchApi<SendPreview>(`${ADMIN_PATH}/issues/${issueId}/send`, { method: 'POST' }),

  getStats: (issueId: string) => fetchApi<IssueStats>(`${ADMIN_PATH}/issues/${issueId}/stats`),

  getPublishedIssues: () => fetchApi<NewsletterIssue[]>(PUBLIC_PATH),
};
